/**
 * FfnBadge — renders the (FFN) Fridge.com Filter Number on a filter page.
 *
 * The filter-side sibling of <FinBadge> and <FpnBadge> (FRIDGE-396, owner
 * ruling 08/17/2026: a filter is NOT a part — a filter carries an FFN, never
 * an FPN and never a FIN). Same monospace chip under the H1, same quiet
 * weight, same click-through to the identifier explainer.
 *
 * Display contract:
 *   - Monospace, uppercase with hyphen ("FFN-0000012") — the FIN's shape, so
 *     the three read as siblings and never as the same identifier
 *   - Renders ONLY a well-formed value from products.ffn (minted in the
 *     database by mint_ffn(); never invented here). Null → nothing, which is
 *     what every filter shows until 20260820213000_ffn_filter_numbers.sql is
 *     applied — Real-or-Hidden, never a placeholder.
 *   - Never rendered off a filter page: the PDP renders <FinBadge> for
 *     appliances, <FpnBadge> for parts and this for filters, decided by
 *     lib/parts/fpn.ts, one switch.
 */

import Link from 'next/link'
import { isValidFfn } from '@/lib/parts/fpn'

interface FfnBadgeProps {
  ffn: string | null | undefined
  className?: string
  /** Already-linked product cards need a label, never a nested link. */
  linked?: boolean
}

export function FfnBadge({ ffn, className = '', linked = true }: FfnBadgeProps) {
  if (!isValidFfn(ffn)) return null
  const badge = (
    <span className="px-1.5 py-0.5 bg-gray-100 rounded border border-gray-200 group-hover:border-[#0469f0]/40 tracking-wider">
      {ffn}
    </span>
  )
  if (!linked) {
    return (
      <span className={`inline-flex items-center gap-1.5 text-xs font-mono text-gray-600 ${className}`}
        title={`Fridge.com Filter Number ${ffn}`} data-testid="ffn-badge">
        {badge}
      </span>
    )
  }
  return (
    <Link
      href="/about-us/ffn"
      className={`inline-flex items-center gap-1.5 text-xs font-mono text-gray-600 hover:text-[#0469f0] transition-colors ${className}`}
      aria-label={`Fridge.com Filter Number ${ffn} — learn more about FFNs`}
      title="Fridge.com Filter Number (FFN) — click to learn more"
      data-testid="ffn-badge"
    >
      {badge}
    </Link>
  )
}

export default FfnBadge
